import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Loader2, Move, Square, Maximize2 } from "lucide-react";

interface ImageRegionSelectorProps {
  imageUrl: string;
  onEdit: (regionDescription: string, editInstructions: string) => Promise<void>;
  onCancel: () => void;
  isEditing: boolean;
}

type RegionPreset = {
  name: string;
  description: string;
};

const regionPresets: RegionPreset[] = [
  { name: "Entire Image", description: "the entire image" },
  { name: "Character/Subject", description: "the main character or subject in the center" },
  { name: "Background", description: "the background area behind the subject" },
  { name: "Top Half", description: "the top half of the image" },
  { name: "Bottom Half", description: "the bottom half of the image" },
  { name: "Left Side", description: "the left side of the image" },
  { name: "Right Side", description: "the right side of the image" },
  { name: "Accessories", description: "any accessories like glasses, hats, or items the character is holding" },
];

export function ImageRegionSelector({ imageUrl, onEdit, onCancel, isEditing }: ImageRegionSelectorProps) {
  const [selectedPreset, setSelectedPreset] = useState<string>("Entire Image");
  const [customRegion, setCustomRegion] = useState<string>("");
  const [editInstructions, setEditInstructions] = useState<string>("");
  const [selectionMode, setSelectionMode] = useState<"preset" | "custom" | "draw" | "fitToScale">("preset");
  const [scaleInstructions, setScaleInstructions] = useState<string>("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [endPoint, setEndPoint] = useState<{ x: number; y: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (selectionMode === "draw" && canvasRef.current && imageRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const img = imageRef.current;

      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        if (startPoint && endPoint) {
          ctx.strokeStyle = "#3b82f6";
          ctx.lineWidth = 3;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(
            Math.min(startPoint.x, endPoint.x),
            Math.min(startPoint.y, endPoint.y),
            Math.abs(endPoint.x - startPoint.x),
            Math.abs(endPoint.y - startPoint.y)
          );
        }
      }
    }
  }, [selectionMode, startPoint, endPoint]);

  const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (selectionMode !== "draw" || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    setStartPoint({ x, y });
    setEndPoint({ x, y });
    setIsDrawing(true);
  };

  const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || selectionMode !== "draw" || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;
    
    setEndPoint({ x, y });
  };

  const handleCanvasMouseUp = () => {
    setIsDrawing(false);
  };

  const getRegionDescription = (): string => {
    if (selectionMode === "preset") {
      const preset = regionPresets.find((p) => p.name === selectedPreset);
      return preset?.description || "the entire image";
    } else if (selectionMode === "custom") {
      return customRegion || "the selected area";
    } else if (selectionMode === "fitToScale") {
      return `the entire image, scaled to: ${scaleInstructions || "user-specified dimensions"}`;
    } else if (selectionMode === "draw" && startPoint && endPoint) {
      const canvas = canvasRef.current;
      if (!canvas) return "the selected area";
      
      const left = Math.min(startPoint.x, endPoint.x);
      const top = Math.min(startPoint.y, endPoint.y);
      const right = Math.max(startPoint.x, endPoint.x);
      const bottom = Math.max(startPoint.y, endPoint.y);
      const width = canvas.width;
      const height = canvas.height;

      const leftPercent = Math.round((left / width) * 100);
      const topPercent = Math.round((top / height) * 100);
      const rightPercent = Math.round((right / width) * 100);
      const bottomPercent = Math.round((bottom / height) * 100);

      return `the rectangular area from ${leftPercent}% left, ${topPercent}% top to ${rightPercent}% right, ${bottomPercent}% bottom`;
    }
    return "the entire image";
  };

  const handleApplyEdit = async () => {
    if (!editInstructions.trim()) return;
    const regionDescription = getRegionDescription();
    await onEdit(regionDescription, editInstructions.trim());
  };

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Edit Region</h3>
        
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button
              type="button"
              variant={selectionMode === "preset" ? "default" : "outline"}
              onClick={() => setSelectionMode("preset")}
              size="sm"
            >
              Preset Regions
            </Button>
            <Button
              type="button"
              variant={selectionMode === "custom" ? "default" : "outline"}
              onClick={() => setSelectionMode("custom")}
              size="sm"
            >
              Custom Description
            </Button>
            <Button
              type="button"
              variant={selectionMode === "draw" ? "default" : "outline"}
              onClick={() => setSelectionMode("draw")}
              size="sm"
            >
              <Square className="mr-2 h-4 w-4" />
              Draw Selection
            </Button>
            <Button
              type="button"
              variant={selectionMode === "fitToScale" ? "default" : "outline"}
              onClick={() => setSelectionMode("fitToScale")}
              size="sm"
            >
              <Maximize2 className="mr-2 h-4 w-4" />
              Fit to Scale
            </Button>
          </div>

          {selectionMode === "preset" && (
            <div className="grid grid-cols-2 gap-2">
              {regionPresets.map((preset) => (
                <Button
                  key={preset.name}
                  type="button"
                  variant={selectedPreset === preset.name ? "default" : "outline"}
                  onClick={() => setSelectedPreset(preset.name)}
                  className="justify-start"
                  size="sm"
                >
                  {preset.name}
                </Button>
              ))}
            </div>
          )}

          {selectionMode === "custom" && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Describe the region to edit
              </label>
              <Textarea
                placeholder="e.g., the owl's face, the name tag area, the background on the left..."
                value={customRegion}
                onChange={(e) => setCustomRegion(e.target.value)}
                className="min-h-[80px]"
                maxLength={200}
              />
              <p className="text-sm text-muted-foreground mt-1">
                {customRegion.length}/200 characters
              </p>
            </div>
          )}

          {selectionMode === "fitToScale" && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Describe how to scale or resize the image
              </label>
              <Textarea
                placeholder="e.g., make it square, fit to 16:9 ratio, scale to 512x512 pixels, crop to 1:1..."
                value={scaleInstructions}
                onChange={(e) => setScaleInstructions(e.target.value)}
                className="min-h-[80px]"
                maxLength={200}
              />
              <p className="text-sm text-muted-foreground mt-1">
                {scaleInstructions.length}/200 characters
              </p>
            </div>
          )}

          {selectionMode === "draw" && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                <Move className="inline h-4 w-4 mr-1" />
                Click and drag on the image to select a region
              </p>
              <div className="relative border rounded-lg overflow-hidden bg-gray-50">
                <img
                  ref={imageRef}
                  src={imageUrl}
                  alt="Edit preview"
                  className="max-w-full h-auto opacity-0 pointer-events-none"
                  onLoad={() => {
                    if (canvasRef.current && imageRef.current) {
                      const canvas = canvasRef.current;
                      const ctx = canvas.getContext("2d");
                      const img = imageRef.current;
                      
                      canvas.width = img.naturalWidth;
                      canvas.height = img.naturalHeight;
                      
                      if (ctx) {
                        ctx.drawImage(img, 0, 0);
                      }
                    }
                  }}
                />
                <canvas
                  ref={canvasRef}
                  className="absolute top-0 left-0 w-full h-auto cursor-crosshair"
                  onMouseDown={handleCanvasMouseDown}
                  onMouseMove={handleCanvasMouseMove}
                  onMouseUp={handleCanvasMouseUp}
                  onMouseLeave={handleCanvasMouseUp}
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">
              What changes do you want to make?
            </label>
            <Textarea
              placeholder="e.g., Change glasses to sunglasses, add a smile, make the background darker, add a construction helmet..."
              value={editInstructions}
              onChange={(e) => setEditInstructions(e.target.value)}
              className="min-h-[100px]"
              maxLength={500}
            />
            <p className="text-sm text-muted-foreground mt-1">
              {editInstructions.length}/500 characters
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-900">
              <strong>Selected region:</strong> {getRegionDescription()}
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Only this area will be modified. The rest of the image will remain unchanged.
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isEditing}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleApplyEdit}
          disabled={isEditing || !editInstructions.trim() || (selectionMode === "fitToScale" && !scaleInstructions.trim())}
        >
          {isEditing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Applying Edit...
            </>
          ) : (
            "Apply Edit"
          )}
        </Button>
      </div>
    </Card>
  );
}
